"use client";

import { useEffect, useRef, useState } from "react";
import { useOpenPanel } from "@openpanel/nextjs";
import { trackGAEvent } from "@/lib/analytics";

const SERVICE = ["Web", "Mobile", "Software", "AI / LLMs", "Cloud", "Security", "Consulting", "Not sure"];
const BUDGET = ["< ₹5L", "₹5–15L", "₹15–50L", "₹50L+", "Tell me"];
const TIMELINE = ["ASAP", "< 1 month", "1–3 months", "3–6 months", "Flexible"];

function ChipGroup({
  options,
  multi,
  accent,
  selected,
  onChange,
}: {
  options: string[];
  multi: boolean;
  accent?: boolean;
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const toggle = (label: string) => {
    if (multi) {
      if (selected.includes(label)) {
        onChange(selected.filter((x) => x !== label));
      } else {
        onChange([...selected, label]);
      }
    } else {
      if (selected.includes(label)) {
        onChange([]);
      } else {
        onChange([label]);
      }
    }
  };

  return (
    <div className="chips">
      {options.map((label) => {
        const on = selected.includes(label);
        const cls = ["chip", accent ? "accent" : "", on ? "on" : ""].filter(Boolean).join(" ");
        return (
          <button
            type="button"
            key={label}
            className={cls}
            onClick={() => toggle(label)}
            aria-pressed={on}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

type ContactFormProps = {
  initialService?: string;
  initialEngagement?: string;
};

export default function ContactForm({
  initialService = "",
  initialEngagement = "",
}: ContactFormProps) {
  const op = useOpenPanel();
  const initialInterested = SERVICE.includes(initialService) ? [initialService] : [];
  const [interested, setInterested] = useState<string[]>(initialInterested);
  const [budget, setBudget] = useState<string[]>([]);
  const [timeline, setTimeline] = useState<string[]>([]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [brief, setBrief] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [refId, setRefId] = useState("QS-000000");
  const [detailsOpen, setDetailsOpen] = useState(Boolean(initialService));
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sent) successRef.current?.focus();
  }, [sent]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          phone,
          company,
          interested,
          budget: budget[0] || "",
          timeline: timeline[0] || "",
          engagement: initialEngagement,
          brief,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit contact request.");
      }

      // Get generated Lead Name from ERPNext CRM or fallback to mock REF
      const referenceCode = data.data?.name || "QS-" + Math.floor(100000 + Math.random() * 900000);
      setRefId(referenceCode);
      setSent(true);

      // OpenPanel Analytics
      op.identify({
        profileId: email,
        firstName: name.split(" ")[0] || "",
        lastName: name.split(" ").slice(1).join(" ") || "",
        email: email,
        properties: {
          phone,
          company,
        },
      });

      op.track("contact_form_submitted", {
        interested: interested.join(", "),
        budget: budget[0] || "",
        timeline: timeline[0] || "",
        refId: referenceCode,
      });

      // Google Analytics
      trackGAEvent(
        "contact_form_submitted",
        "engagement",
        `Services: ${interested.join(", ")}; Budget: ${budget[0] || "None"}; Timeline: ${timeline[0] || "None"}`
      );
    } catch (err: any) {
      console.error("Submission error:", err);
      setError(err.message || "Unable to send brief. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-card reveal">
      <div className="form-head">
        <span>Send us a brief</span>
        <span>Reply within 24 hrs</span>
      </div>

      {error && (
        <div role="alert" aria-live="assertive" className="form-error">
          <b>We couldn&apos;t send your brief.</b> {error}
        </div>
      )}

      {!sent && (
        <form id="briefForm" onSubmit={onSubmit}>
          <p className="form-intro">
            Tell us what you&apos;re building and we&apos;ll get back within 24 hours — with a
            proposal, a call link, or honest pushback. No obligation, and the first call&apos;s on us.
          </p>
          {(initialService || initialEngagement) && (
            <div className="form-context" aria-label="Your selected enquiry context">
              <span>Your enquiry</span>
              {initialService && <b>{initialService}</b>}
              {initialEngagement && <b>{initialEngagement}</b>}
            </div>
          )}
          <div className="form-step">
            <div className="step-label" data-n="01">
              About you
            </div>
            <div className="input-row">
              <div className="field">
                <label className="field-label" htmlFor="cf-name">
                  Full name <span className="req">*</span>
                </label>
                <input 
                  id="cf-name" 
                  className="input" 
                  required 
                  autoComplete="name"
                  placeholder="Jane Doe" 
                  name="name" 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="cf-email">
                  Work email <span className="req">*</span>
                </label>
                <input 
                  id="cf-email" 
                  className="input" 
                  required 
                  type="email" 
                  autoComplete="email"
                  placeholder="jane@company.com" 
                  name="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          <div className="form-step">
            <label className="step-label" data-n="02" htmlFor="cf-brief" style={{ display: "flex" }}>
              Describe the problem
            </label>
            <div className="field">
              <textarea
                id="cf-brief"
                className="input"
                placeholder="In 2–3 sentences — what are you trying to ship, and what's in the way?"
                name="brief"
                required
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <details
            className="form-details"
            open={detailsOpen}
            onToggle={(event) => setDetailsOpen(event.currentTarget.open)}
          >
            <summary>
              <span>Add project details</span>
              <span>Optional · helps us prepare</span>
            </summary>
            <div className="form-details-body">
              <div className="form-step">
                <div className="step-label" data-n="03">
                  Additional contact details
                </div>
                <div className="input-row">
                  <div className="field">
                    <label className="field-label" htmlFor="cf-company">
                      Company <span className="optional">optional</span>
                    </label>
                    <input
                      id="cf-company"
                      className="input"
                      autoComplete="organization"
                      placeholder="Company name"
                      name="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor="cf-phone">
                      Phone <span className="optional">optional</span>
                    </label>
                    <input
                      id="cf-phone"
                      className="input"
                      type="tel"
                      autoComplete="tel"
                      placeholder="Optional"
                      name="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
              <div className="form-step">
                <div className="step-label" data-n="04">
                  I&apos;m interested in
                </div>
                <ChipGroup options={SERVICE} multi selected={interested} onChange={setInterested} />
              </div>
              <div className="form-step">
                <div className="step-label" data-n="05">
                  Budget
                </div>
                <ChipGroup options={BUDGET} multi={false} accent selected={budget} onChange={setBudget} />
              </div>
              <div className="form-step">
                <div className="step-label" data-n="06">
                  Timeline
                </div>
                <ChipGroup options={TIMELINE} multi={false} selected={timeline} onChange={setTimeline} />
              </div>
            </div>
          </details>
          <div className="form-submit">
            <div className="form-meta">
              We reply within 24 hrs · Mon–Fri<br />Your details are used only to respond to this enquiry.
            </div>
            <button type="submit" className="submit-btn" disabled={loading} style={{ opacity: loading ? 0.7 : 1 }}>
              {loading ? "Sending..." : "Send brief"} <span className="arrow">→</span>
            </button>
          </div>
        </form>
      )}

      <div
        ref={successRef}
        className={sent ? "success on" : "success"}
        role="status"
        aria-live="polite"
        tabIndex={sent ? -1 : undefined}
      >
        <div className="success-mark">
          <svg viewBox="0 0 24 24">
            <path d="M4 12l5 5L20 6" />
          </svg>
        </div>
        <h2>Brief received.</h2>
        <p>
          We&apos;ll read it properly, then reply in the next 24 hours with either a proposal or
          questions.
        </p>
        <div className="id">REF · {refId}</div>
      </div>
    </div>
  );
}
